// Pallet Optimizer — upload a Sales Order Detail export, pack each PO onto the
// fewest pallets using item dims from Items Detail + a fixed capacity table.
// No dims? Borrow from a same-design/size sibling (different color). Still none
// -> flagged (not silently mis-packed).
import React, { useCallback, useState } from 'react';
import {
  Box, Paper, Typography, Button, Stack, Alert, AlertTitle, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tab,
  Chip, Tooltip, LinearProgress, Divider, List, ListItem, ListItemText,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import * as XLSX from 'xlsx';
import { API_ENDPOINTS } from '../config/api';

const Stat = ({ label, value, sub, color }) => (
  <Paper variant="outlined" sx={{ p: 2, minWidth: 140, flex: '1 1 140px' }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2, color }}>{value}</Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
  </Paper>
);

// Persistent right-hand explainer: how it works + the packing rules.
const RulesPanel = () => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>How it works</Typography>
    <List dense disablePadding>
      {[
        'Upload the Sales Order Detail export (SO#, PO#, Item ID, ORDQ).',
        'Each item’s package dimensions are pulled from Items Detail (Package L×W×H) — never from the order file.',
        'Dimensions map to a capacity class (units that fit on a single vs a double pallet).',
        'Each PO (one SO#) is packed onto the fewest pallets.',
        'No dims? Borrow from a same-design/size sibling (different color). If none exists, the item is flagged.',
      ].map((t, i) => (
        <ListItem key={i} sx={{ py: 0.25, alignItems: 'flex-start' }}>
          <Box sx={{ minWidth: 22, fontWeight: 700, color: 'primary.main' }}>{i + 1}.</Box>
          <ListItemText primary={t} primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
      ))}
    </List>

    <Divider sx={{ my: 1.5 }} />
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Packing rules</Typography>
    <List dense disablePadding sx={{ listStyleType: 'disc', pl: 2 }}>
      {[
        'One SO# = one PO; quantities come from ORDQ.',
        'Any 93×8×8 item forces the whole PO to double-stacked pallets; otherwise single.',
        'Each unit uses 1 / capacity of a pallet; pallets fill to 110%, with a 15% tolerance to avoid splitting one SKU.',
        'Bulkiest items are placed first; the emptiest open pallet is filled before a new one is started.',
        'Grand-total / footer rows are ignored.',
      ].map((t, i) => (
        <ListItem key={i} sx={{ display: 'list-item', py: 0.25, pl: 0 }}>
          <ListItemText primary={t} primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
      ))}
    </List>

    <Divider sx={{ my: 1.5 }} />
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>Sibling dimension inference</Typography>
    <Typography variant="body2" color="text.secondary">
      SKUs are <b>{'{design}{color}-{size}'}</b> (e.g. <code>ABT141A-8</code>). Color doesn’t change the box,
      so a SKU missing dims borrows them from a sibling with the same design and size but a different color
      — e.g. <code>ABT141A-8</code> has no dims, but <code>ABT141B-8</code> is 93×8×8, so that’s used.
      These are listed under <b>Inferred Dims</b> so you can verify and update the master.
    </Typography>
  </Paper>
);

export default function PalletOptimizer() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [tab, setTab] = useState(0);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) { setFile(f); setError(''); setResult(null); }
  };

  const optimize = useCallback(async () => {
    if (!file) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const token = localStorage.getItem('jwtToken');
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(API_ENDPOINTS.PALLET_OPTIMIZER_OPTIMIZE, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        let msg = data.message || 'Optimization failed.';
        if (data.detectedColumns) msg += '  Detected columns: ' + data.detectedColumns.join(', ');
        setError(msg);
        return;
      }
      setResult(data); setTab(0);
    } catch (e) {
      setError('Request failed: ' + (e.message || e));
    } finally {
      setLoading(false);
    }
  }, [file]);

  const exportExcel = useCallback(() => {
    if (!result) return;
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(result.palletDetail.map(p => ({
      'SO#': p.so, 'PO#': p.po, 'Ship To': p.shipTo, 'WHS': p.whs, 'Pallet#': p.palletNo,
      'Type': p.type, 'Units': p.units, 'Util %': p.utilPct + '%', 'SKUs Mixed': p.skusMixed,
      'Strategy': p.strategy, 'Item Breakdown': p.itemBreakdown,
    }))), 'Pallet Detail');

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(result.poSummary.map(p => ({
      'SO#': p.so, 'PO#': p.po, 'Ship To': p.shipTo, 'WHS': p.whs,
      'Total Weight (lb)': p.totalWeight, 'Strategy': p.strategy,
      'Pallets': p.pallets, 'Units': p.units, 'Double': p.doubleP, 'Single': p.singleP,
      'Avg Util %': p.avgUtilPct + '%', 'Flagged Items': p.flaggedItems,
    }))), 'PO Summary');

    const s = result.summary;
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['PALLET OPTIMIZATION REPORT', ''],
      ['Source File', result.sourceFile || ''],
      ['', ''],
      ['INPUT', ''],
      ['Purchase Orders (SO#)', s.purchaseOrders],
      ['Order Lines', s.orderLines],
      ['Total Units (ORDQ)', s.totalUnits],
      ['Distinct SKUs', s.distinctSkus],
      ['', ''],
      ['RESULT', ''],
      ['Optimized Pallets', s.optimizedPallets],
      ['Baseline Pallets (1 per line)', s.baselinePallets],
      ['Pallet Reduction', s.palletReductionPct + '%'],
      ['Double Pallets', s.doublePallets],
      ['Single Pallets', s.singlePallets],
      ['Average Utilization', s.avgUtilPct + '%'],
      ['Dims Inferred From Sibling', s.inferredCount],
      ['Flagged Items (no/unknown dims)', s.flaggedCount],
    ]), 'Optimization Summary');

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      (result.inferred.length ? result.inferred : [{ itemId: '(none)', inferredDim: '', sourceSibling: '', sourceDim: '' }]).map(f => ({
        'Item ID': f.itemId, 'Inferred Dim': f.inferredDim, 'From Sibling': f.sourceSibling, 'Sibling Dim': f.sourceDim,
      }))), 'Inferred Dims');

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      (result.flagged.length ? result.flagged : [{ itemId: '(none)', dim: '', reason: '' }]).map(f => ({
        'Item ID': f.itemId, 'Dims Found': f.dim, 'Reason': f.reason,
      }))), 'Flagged Items');

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(result.dimCapacityRef.map(d => ({
      'Dims (in)': d.dims, 'Single Max': d.singleMax, 'Double Max': d.doubleMax, 'Needs Double?': d.needsDouble, 'Status': d.status,
    }))), 'Dim Capacity Ref');

    XLSX.writeFile(wb, `Pallet_Optimization_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }, [result]);

  const s = result?.summary;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1600, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Pallet Optimizer</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload a Sales Order Detail export. Each PO is packed onto the fewest pallets using item
        dimensions from Items Detail and the pallet capacity table.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2, alignItems: 'flex-start' }}>
        {/* LEFT: upload + results */}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />}>
                Choose file
                <input hidden type="file" accept=".xls,.xlsx" onChange={onPick} />
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                {file ? file.name : 'No file selected (.xls or .xlsx)'}
              </Typography>
              <Button variant="contained" disabled={!file || loading} onClick={optimize}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}>
                {loading ? 'Optimizing…' : 'Optimize Pallets'}
              </Button>
              {result && (
                <Button variant="outlined" color="success" startIcon={<DownloadIcon />} onClick={exportExcel}>
                  Export Excel
                </Button>
              )}
            </Stack>
            {loading && <LinearProgress sx={{ mt: 2 }} />}
          </Paper>

          {error && <Alert severity="error" sx={{ mb: 2 }}><AlertTitle>Error</AlertTitle>{error}</Alert>}

          {result && s && (
            <>
              <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mb: 2 }}>
                <Stat label="Optimized Pallets" value={s.optimizedPallets} sub={`from ${s.baselinePallets} lines`} />
                <Stat label="Pallet Reduction" value={`${s.palletReductionPct}%`} />
                <Stat label="Purchase Orders" value={s.purchaseOrders} sub={`${s.orderLines} lines`} />
                <Stat label="Total Units" value={s.totalUnits} sub={`${s.distinctSkus} SKUs`} />
                <Stat label="Double / Single" value={`${s.doublePallets} / ${s.singlePallets}`} />
                <Stat label="Avg Utilization" value={`${s.avgUtilPct}%`} />
                <Stat label="Inferred Dims" value={s.inferredCount} sub="from sibling" color="info.main" />
                <Stat label="Flagged" value={s.flaggedCount} sub="need dim setup" color={s.flaggedCount ? 'warning.main' : undefined} />
              </Stack>

              {s.inferredCount > 0 && (
                <Alert severity="info" icon={<AutoFixHighIcon />} sx={{ mb: 2 }}>
                  <AlertTitle>{s.inferredCount} item(s) used inferred dimensions</AlertTitle>
                  These SKUs had no dimensions in Items Detail, so dimensions were borrowed from a same-design/size
                  sibling (different color) and used for packing — see the “Inferred Dims” tab. Verify and update
                  the master when you can.
                </Alert>
              )}
              {s.flaggedCount > 0 && (
                <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
                  <AlertTitle>{s.flaggedCount} item(s) flagged</AlertTitle>
                  These items have no dimensions in Items Detail and no same-design/size sibling to borrow from, so
                  they couldn’t be packed — see the “Flagged Items” tab.
                </Alert>
              )}

              <Paper variant="outlined">
                <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
                  <Tab label={`PO Summary (${result.poSummary.length})`} />
                  <Tab label={`Pallet Detail (${result.palletDetail.length})`} />
                  <Tab label={`Inferred Dims (${result.inferred.length})`} />
                  <Tab label={`Flagged Items (${result.flagged.length})`} />
                  <Tab label={`Dim Mapping (${result.dimMappings.length})`} />
                  <Tab label="Dim Capacity Ref" />
                </Tabs>
                <Divider />

                {tab === 0 && (
                  <TableContainer sx={{ maxHeight: 560 }}>
                    <Table size="small" stickyHeader>
                      <TableHead><TableRow>
                        {['SO#', 'PO#', 'Ship To', 'WHS', 'Total Weight (lb)', 'Strategy', 'Pallets', 'Units', 'Double', 'Single', 'Avg Util %', 'Flagged'].map(h =>
                          <TableCell key={h}>{h}</TableCell>)}
                      </TableRow></TableHead>
                      <TableBody>
                        {result.poSummary.map((p, i) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{p.so}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{p.po}</TableCell>
                            <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <Tooltip title={p.shipTo || ''}><span>{p.shipTo || '—'}</span></Tooltip>
                            </TableCell>
                            <TableCell>{p.whs}</TableCell>
                            <TableCell>
                              {(p.totalWeight ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                              {p.unweighedItems > 0 && (
                                <Tooltip title={`${p.unweighedItems} item${p.unweighedItems === 1 ? '' : 's'} on this PO have no weight in Items Detail, so the total is understated`}>
                                  <span style={{ marginLeft: 4, cursor: 'help', opacity: 0.7 }}>*</span>
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell><Chip size="small" variant="outlined"
                              color={p.strategy === 'all_double' ? 'secondary' : 'default'} label={p.strategy} /></TableCell>
                            <TableCell>{p.pallets}</TableCell>
                            <TableCell>{p.units}</TableCell>
                            <TableCell>{p.doubleP}</TableCell>
                            <TableCell>{p.singleP}</TableCell>
                            <TableCell>{p.avgUtilPct}%</TableCell>
                            <TableCell>{p.flaggedItems > 0 ? <Chip size="small" color="warning" label={p.flaggedItems} /> : '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {tab === 1 && (
                  <TableContainer sx={{ maxHeight: 560 }}>
                    <Table size="small" stickyHeader>
                      <TableHead><TableRow>
                        {['SO#', 'PO#', 'Ship To', 'WHS', 'Pallet#', 'Type', 'Units', 'Util %', 'SKUs', 'Strategy', 'Item Breakdown'].map(h =>
                          <TableCell key={h}>{h}</TableCell>)}
                      </TableRow></TableHead>
                      <TableBody>
                        {result.palletDetail.map((p, i) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{p.so}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{p.po}</TableCell>
                            <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <Tooltip title={p.shipTo || ''}><span>{p.shipTo || '—'}</span></Tooltip>
                            </TableCell>
                            <TableCell>{p.whs}</TableCell>
                            <TableCell>{p.palletNo}</TableCell>
                            <TableCell><Chip size="small" variant="outlined"
                              color={p.type === 'Double' ? 'secondary' : 'default'} label={p.type} /></TableCell>
                            <TableCell>{p.units}</TableCell>
                            <TableCell>{p.utilPct}%</TableCell>
                            <TableCell>{p.skusMixed}</TableCell>
                            <TableCell>{p.strategy}</TableCell>
                            <TableCell sx={{ maxWidth: 360, fontSize: 12 }}>
                              <Tooltip title={p.itemBreakdown || ''}>
                                <span style={{ display: 'inline-block', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                                  {p.itemBreakdown}
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {tab === 2 && (
                  <TableContainer sx={{ maxHeight: 560 }}>
                    <Table size="small" stickyHeader>
                      <TableHead><TableRow>
                        {['Item ID', 'Inferred Dim', 'From Sibling', 'Sibling Dim', 'Note'].map(h => <TableCell key={h}>{h}</TableCell>)}
                      </TableRow></TableHead>
                      <TableBody>
                        {result.inferred.length === 0 && (
                          <TableRow><TableCell colSpan={5} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No inferred dimensions — every SKU had its own dims.</Typography>
                          </TableCell></TableRow>
                        )}
                        {result.inferred.map((f, i) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{f.itemId}</TableCell>
                            <TableCell><Chip size="small" color="info" variant="outlined" label={f.inferredDim} /></TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{f.sourceSibling}</TableCell>
                            <TableCell>{f.sourceDim}</TableCell>
                            <TableCell sx={{ fontSize: 12 }}>{f.message}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {tab === 3 && (
                  <TableContainer sx={{ maxHeight: 560 }}>
                    <Table size="small" stickyHeader>
                      <TableHead><TableRow>
                        {['Item ID', 'Dims Found', 'Reason'].map(h => <TableCell key={h}>{h}</TableCell>)}
                      </TableRow></TableHead>
                      <TableBody>
                        {result.flagged.length === 0 && (
                          <TableRow><TableCell colSpan={3} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No flagged items — every SKU mapped (or was inferred) to a capacity class.</Typography>
                          </TableCell></TableRow>
                        )}
                        {result.flagged.map((f, i) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{f.itemId}</TableCell>
                            <TableCell>{f.dim || '—'}</TableCell>
                            <TableCell>{f.reason}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {tab === 4 && (
                  <TableContainer sx={{ maxHeight: 560 }}>
                    <Table size="small" stickyHeader>
                      <TableHead><TableRow>
                        {['Item ID', 'Dims Found', 'Mapped To', 'Match', 'Note'].map(h => <TableCell key={h}>{h}</TableCell>)}
                      </TableRow></TableHead>
                      <TableBody>
                        {result.dimMappings.length === 0 && (
                          <TableRow><TableCell colSpan={5} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>Every SKU matched a capacity class exactly — nothing rounded.</Typography>
                          </TableCell></TableRow>
                        )}
                        {result.dimMappings.map((m, i) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{m.itemId}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{m.rawDim}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{m.mappedTo}</TableCell>
                            <TableCell>{m.matchType === 'rounded'
                              ? <Chip size="small" color="success" variant="outlined" label="rounded" />
                              : m.matchType === 'folded'
                                ? <Chip size="small" color="primary" variant="outlined" label="folded" />
                                : <Chip size="small" color="warning" variant="outlined" label="nearest" />}</TableCell>
                            <TableCell sx={{ fontSize: 12 }}>{m.note}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {tab === 5 && (
                  <TableContainer sx={{ maxHeight: 560 }}>
                    <Table size="small" stickyHeader>
                      <TableHead><TableRow>
                        {['Dims (in)', 'Single Max', 'Double Max', 'Needs Double?', 'Status'].map(h => <TableCell key={h}>{h}</TableCell>)}
                      </TableRow></TableHead>
                      <TableBody>
                        {result.dimCapacityRef.map((d, i) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{d.dims}</TableCell>
                            <TableCell>{d.singleMax}</TableCell>
                            <TableCell>{d.doubleMax}</TableCell>
                            <TableCell>{d.needsDouble === 'Yes'
                              ? <Chip size="small" color="secondary" label="Yes" /> : 'No'}</TableCell>
                            <TableCell>{d.status === 'pending'
                              ? <Chip size="small" color="warning" variant="outlined" label="pending — set capacity" />
                              : <Chip size="small" color="success" variant="outlined" label="active" />}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </>
          )}
        </Box>

        {/* RIGHT: rules / how it works */}
        <Box sx={{ width: { xs: '100%', lg: 360 }, flexShrink: 0 }}>
          <RulesPanel />
        </Box>
      </Box>
    </Box>
  );
}
